import { useEffect, useState } from 'react';
import api from '../api/client';
import { useFincaId } from '../context/FincaContext';
import PiscinaBuscador from '../components/PiscinaBuscador';
import { format } from 'date-fns';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';

/* ────────────────────────────────────────────
   Helpers ISO 8601 (mismos que ReporteRegistroDiario)
   ──────────────────────────────────────────── */
function isoDate(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getLunesYDomingoDeSemana(year, semana) {
  const ene4 = new Date(Date.UTC(year, 0, 4));
  const dow = ene4.getUTCDay() || 7;
  const lunesSem1 = new Date(ene4);
  lunesSem1.setUTCDate(ene4.getUTCDate() - dow + 1);

  const lunes = new Date(lunesSem1);
  lunes.setUTCDate(lunesSem1.getUTCDate() + (semana - 1) * 7);

  const domingo = new Date(lunes);
  domingo.setUTCDate(lunes.getUTCDate() + 6);

  return { lunes: isoDate(lunes), domingo: isoDate(domingo) };
}

const fmtCorto = (iso) => {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

function formatearFechaCorta(fecha) {
  if (!fecha) return '';
  const [anio, mes, dia] = fecha.split('-');
  return `${parseInt(dia)}/${parseInt(mes)}/${anio}`;
}

function diasEntre(fechaA, fechaB) {
  if (!fechaA || !fechaB) return null;
  const d = (new Date(fechaB) - new Date(fechaA)) / 86400000;
  return d > 0 ? d : null;
}

export default function Historico() {
  const fincaIdContext = useFincaId();
  const [sectores, setSectores] = useState([]);
  const [piscinas, setPiscinas] = useState([]);
  const [corridas, setCorridas] = useState([]);

  const [sectorId, setSectorId] = useState('');
  const [piscinaId, setPiscinaId] = useState('');
  const [siembraId, setSiembraId] = useState('');

  /* ✅ Modo de fecha: rango | semana | rangoSemanas */
  const [modoFecha, setModoFecha] = useState('rango');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  const [semanaDesdeKey, setSemanaDesdeKey] = useState('');
  const [semanaHastaKey, setSemanaHastaKey] = useState('');
  const [semanasDisponibles, setSemanasDisponibles] = useState([]);
  const [cargandoSemanas, setCargandoSemanas] = useState(false);

  const [historial, setHistorial] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const [mostrarResumen, setMostrarResumen] = useState(true);

  useEffect(() => {
    cargarSectores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaIdContext]);

  useEffect(() => {
    if (sectorId) {
      cargarPiscinas(sectorId);
    } else {
      setPiscinas([]);
    }
    setPiscinaId('');
    setSiembraId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorId]);

  useEffect(() => {
    if (piscinaId) {
      cargarCorridas(piscinaId);
    } else {
      setCorridas([]);
    }
    setSiembraId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [piscinaId]);

  /* ✅ Cargar semanas disponibles cuando hay piscina + corrida y el modo NO es 'rango' */
  useEffect(() => {
    if (modoFecha === 'rango') {
      setSemanasDisponibles([]);
      return;
    }
    if (!piscinaId || !siembraId) {
      setSemanasDisponibles([]);
      return;
    }

    let activo = true;
    setCargandoSemanas(true);

    const params = { piscina_id: piscinaId, siembra_id: siembraId };

    api.get('/muestreos/semanas-con-datos', { params })
      .then(({ data }) => {
        if (!activo) return;
        const conRango = (data || []).map((s) => {
          const { lunes, domingo } = getLunesYDomingoDeSemana(s.year, s.semana);
          return { ...s, lunes, domingo, key: `${s.year}-${String(s.semana).padStart(2, '0')}` };
        });
        setSemanasDisponibles(conRango);
        if (conRango.length > 0) {
          const primera = conRango[0];
          if (!semanaDesdeKey) {
            setSemanaDesdeKey(primera.key);
            setFechaDesde(primera.lunes);
          }
          if (!semanaHastaKey) {
            setSemanaHastaKey(primera.key);
            setFechaHasta(primera.domingo);
          }
        } else {
          // Sin semanas con datos → limpiar rango
          setFechaDesde('');
          setFechaHasta('');
        }
      })
      .catch(() => { if (activo) setSemanasDisponibles([]); })
      .finally(() => { if (activo) setCargandoSemanas(false); });

    return () => { activo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoFecha, piscinaId, siembraId]);

  useEffect(() => {
    if (piscinaId && siembraId) {
      cargarHistorial();
    } else {
      setHistorial(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siembraId, fechaDesde, fechaHasta]);

  const cargarSectores = async () => {
    try {
      if (!fincaIdContext) {
        setSectores([]);
        return;
      }
      const { data: secs } = await api.get(`/sectores/finca/${fincaIdContext}`);
      setSectores(secs);
    } catch (err) {
      console.error(err);
    }
  };

  const cargarPiscinas = async (id) => {
    try {
      const { data } = await api.get(`/muestreos/piscinas-con-datos?sector_id=${id}`);
      setPiscinas(data);
    } catch (err) {
      console.error(err);
      setPiscinas([]);
    }
  };

  const cargarCorridas = async (id) => {
    try {
      const { data } = await api.get(`/siembras/piscina/${id}`);
      setCorridas(data);
      if (data.length > 0) setSiembraId(data[0].id);
    } catch (err) {
      console.error(err);
      setCorridas([]);
    }
  };

  const cargarHistorial = async () => {
    setCargando(true);
    setError(null);
    try {
      const params = { siembra_id: siembraId };
      if (fechaDesde) params.desde = fechaDesde;
      if (fechaHasta) params.hasta = fechaHasta + 'T23:59:59';

      const { data } = await api.get(`/muestreos/piscina/${piscinaId}`, { params });
      setHistorial(data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Error al cargar historial');
    } finally {
      setCargando(false);
    }
  };

  const exportarExcel = async () => {
    if (!piscinaId || !siembraId) return;
    try {
      const params = { siembra_id: siembraId };
      if (fechaDesde) params.desde = fechaDesde;
      if (fechaHasta) params.hasta = fechaHasta;

      const res = await api.get(
        `/muestreos/piscina/${piscinaId}/exportar/excel`,
        { params, responseType: 'blob' }
      );

      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crecimientos_${piscinaId}_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando:', err);
      alert('Error al exportar. Revisa la consola.');
    }
  };

  /* ✅ Cambio de modo */
  const cambiarModo = (nuevo) => {
    setModoFecha(nuevo);
    if (nuevo === 'rango') {
      // Al volver a rango, limpio semanas
      setSemanaDesdeKey('');
      setSemanaHastaKey('');
    } else {
      // Al entrar a semana o rangoSemanas, reseteo el rango
      setSemanaDesdeKey('');
      setSemanaHastaKey('');
      setFechaDesde('');
      setFechaHasta('');
    }
  };

  const cambiarSemanaUnica = (key) => {
    setSemanaDesdeKey(key);
    setSemanaHastaKey(key);
    const sem = semanasDisponibles.find((s) => s.key === key);
    if (sem) { setFechaDesde(sem.lunes); setFechaHasta(sem.domingo); }
  };

  const cambiarSemanaDesde = (key) => {
    setSemanaDesdeKey(key);
    const sem = semanasDisponibles.find((s) => s.key === key);
    if (!sem) return;
    setFechaDesde(sem.lunes);
    const semHasta = semanasDisponibles.find((s) => s.key === semanaHastaKey);
    if (!semHasta || sem.lunes > semHasta.lunes) {
      setSemanaHastaKey(key);
      setFechaHasta(sem.domingo);
    }
  };

  const cambiarSemanaHasta = (key) => {
    setSemanaHastaKey(key);
    const sem = semanasDisponibles.find((s) => s.key === key);
    if (!sem) return;
    setFechaHasta(sem.domingo);
    const semDesde = semanasDisponibles.find((s) => s.key === semanaDesdeKey);
    if (!semDesde || sem.domingo < semDesde.domingo) {
      setSemanaDesdeKey(key);
      setFechaDesde(sem.lunes);
    }
  };

  const limpiarFechas = () => {
    if (modoFecha === 'rango') {
      setFechaDesde('');
      setFechaHasta('');
    } else {
      setSemanaDesdeKey('');
      setSemanaHastaKey('');
      setFechaDesde('');
      setFechaHasta('');
    }
  };

  const pesoSiembra = historial?.siembra?.peso_inicial_g != null
    ? Number(historial.siembra.peso_inicial_g)
    : null;
  const fechaSiembra = historial?.siembra?.fecha_siembra || null;
  const cantidadSembrada = historial?.siembra?.cantidad_sembrada != null
    ? Number(historial.siembra.cantidad_sembrada)
    : null;

  const diasCultivo = fechaSiembra
    ? Math.floor((new Date() - new Date(fechaSiembra)) / 86400000)
    : null;

  const hayMuestreos = (historial?.muestreos?.length || 0) > 0;
  const ultimoMuestreo = historial?.muestreos?.[0];

  const hayFiltroFecha = !!(fechaDesde || fechaHasta);

  const calcCrecLineal = (pesoMuestreo, fechaMuestreo) => {
    if (pesoMuestreo == null || pesoSiembra == null || !fechaSiembra) return null;
    const d = diasEntre(fechaSiembra, fechaMuestreo);
    if (!d) return null;
    return ((Number(pesoMuestreo) - pesoSiembra) * 7) / d;
  };

  const datosGrafico = (() => {
    if (!historial) return [];

    const datos = [...historial.muestreos]
      .reverse()
      .map((m) => ({
        fecha: format(new Date(m.fecha_hora), 'dd/MM'),
        fechaCompleta: format(new Date(m.fecha_hora), 'dd/MM/yyyy HH:mm'),
        peso: Number(Number(m.peso_promedio_g).toFixed(2)),
        animales: m.cantidad_muestreada,
        porcentaje: m.porcentaje != null ? Number(m.porcentaje) : null,
        fca: m.fca != null ? Number(m.fca) : null,
        tipo: 'muestreo',
      }));

    if (historial.siembra?.fecha_siembra && historial.siembra?.peso_inicial_g != null) {
      datos.unshift({
        fecha: format(new Date(historial.siembra.fecha_siembra), 'dd/MM'),
        fechaCompleta: format(new Date(historial.siembra.fecha_siembra), 'dd/MM/yyyy') + ' (siembra)',
        peso: Number(Number(historial.siembra.peso_inicial_g).toFixed(2)),
        animales: historial.siembra.cantidad_sembrada,
        porcentaje: 100,
        fca: null,
        tipo: 'siembra',
      });
    }

    return datos;
  })();

  const hayFca = datosGrafico.some((d) => d.fca != null);

  const kpiUltimoPeso = hayMuestreos
    ? Number(ultimoMuestreo.peso_promedio_g)
    : pesoSiembra;

  const kpiGanancia = kpiUltimoPeso != null && pesoSiembra != null
    ? kpiUltimoPeso - pesoSiembra
    : null;

  const kpiAnimales = hayMuestreos
    ? ultimoMuestreo.cantidad_muestreada
    : cantidadSembrada;

  const kpiSobrevivencia = !hayMuestreos
    ? 100
    : (ultimoMuestreo?.porcentaje != null ? Number(ultimoMuestreo.porcentaje) : null);

  const kpiCrecLineal = (() => {
    if (kpiUltimoPeso == null || pesoSiembra == null) return null;
    if (!diasCultivo || diasCultivo <= 0) return null;
    return ((kpiUltimoPeso - pesoSiembra) * 7) / diasCultivo;
  })();

  const mostrarPanelCompleto = !cargando && historial && (hayMuestreos || !hayFiltroFecha);

  const semDesde = semanasDisponibles.find((s) => s.key === semanaDesdeKey);
  const semHasta = semanasDisponibles.find((s) => s.key === semanaHastaKey);
  const aniosDisponibles = [...new Set(semanasDisponibles.map((s) => s.year))].sort((a, b) => b - a);

  const inputBase = 'w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';
  const labelBase = 'text-[10px] font-medium text-gray-500';

  return (
    <div className="p-1">
      {/* Filtros — STICKY */}
      <div className="sticky top-0 z-30 -mx-6 px-6 pt-6 pb-4 bg-gray-100 mb-2">
        <div className="bg-white p-4 rounded shadow">
          <div className="flex items-center gap-6 flex-wrap justify-center">

            {/* Col 1: Toggle 3 modos */}
            <div className="flex flex-col gap-1.5">
              <button
                type="button"
                onClick={() => cambiarModo('rango')}
                className={`px-4 py-1.5 rounded-lg text-[11px] font-medium transition whitespace-nowrap ${
                  modoFecha === 'rango' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📆 Rango días
              </button>
              <button
                type="button"
                onClick={() => cambiarModo('semana')}
                disabled={!piscinaId || !siembraId}
                className={`px-4 py-1.5 rounded-lg text-[11px] font-medium transition whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
                  modoFecha === 'semana' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📊 Semana
              </button>
              <button
                type="button"
                onClick={() => cambiarModo('rangoSemanas')}
                disabled={!piscinaId || !siembraId}
                className={`px-4 py-1.5 rounded-lg text-[11px] font-medium transition whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
                  modoFecha === 'rangoSemanas' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📈 Rango semanas
              </button>
            </div>

            {/* Col 2: Fechas / Semanas */}
            <div className="flex flex-col gap-1.5 min-w-[180px]">
              {modoFecha === 'rango' && (
                <>
                  <label className="block">
                    <span className={labelBase}>Desde</span>
                    <input
                      type="date"
                      value={fechaDesde}
                      onChange={(e) => setFechaDesde(e.target.value)}
                      className={`mt-0.5 ${inputBase}`}
                    />
                  </label>
                  <label className="block">
                    <span className={labelBase}>Hasta</span>
                    <input
                      type="date"
                      value={fechaHasta}
                      onChange={(e) => setFechaHasta(e.target.value)}
                      className={`mt-0.5 ${inputBase}`}
                    />
                  </label>
                </>
              )}

              {modoFecha === 'semana' && (
                <>
                  <label className="block">
                    <span className={labelBase}>Semana</span>
                    <select
                      value={semanaDesdeKey}
                      onChange={(e) => cambiarSemanaUnica(e.target.value)}
                      disabled={cargandoSemanas || semanasDisponibles.length === 0}
                      className={`mt-0.5 min-w-[280px] ${inputBase}`}
                    >
                      {cargandoSemanas ? <option value="">Cargando...</option>
                        : semanasDisponibles.length === 0 ? <option value="">Sin datos</option>
                        : (<>
                            <option value="">— Seleccionar —</option>
                            {aniosDisponibles.map((anio) => {
                              const semanasAnio = semanasDisponibles.filter((s) => s.year === anio);
                              return (
                                <optgroup key={anio} label={`Año ${anio}`}>
                                  {semanasAnio.map((s) => (
                                    <option key={s.key} value={s.key}>
                                      Semana {String(s.semana).padStart(2, '0')}: {fmtCorto(s.lunes)} → {fmtCorto(s.domingo)} · {s.total} reg.
                                    </option>
                                  ))}
                                </optgroup>
                              );
                            })}
                          </>)}
                    </select>
                  </label>
                  {semDesde && (
                    <div className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1 whitespace-nowrap">
                      📊 Semana {String(semDesde.semana).padStart(2, '0')} · {semDesde.year}: {fmtCorto(semDesde.lunes)} → {fmtCorto(semDesde.domingo)}
                    </div>
                  )}
                </>
              )}

              {modoFecha === 'rangoSemanas' && (
                <>
                  <label className="block">
                    <span className={labelBase}>Semana desde</span>
                    <select
                      value={semanaDesdeKey}
                      onChange={(e) => cambiarSemanaDesde(e.target.value)}
                      disabled={cargandoSemanas || semanasDisponibles.length === 0}
                      className={`mt-0.5 min-w-[240px] ${inputBase}`}
                    >
                      {cargandoSemanas ? <option value="">Cargando...</option>
                        : semanasDisponibles.length === 0 ? <option value="">Sin datos</option>
                        : (<>
                            <option value="">— Desde —</option>
                            {aniosDisponibles.map((anio) => {
                              const semanasAnio = semanasDisponibles.filter((s) => s.year === anio);
                              return (
                                <optgroup key={anio} label={`Año ${anio}`}>
                                  {semanasAnio.map((s) => (
                                    <option key={s.key} value={s.key}>
                                      S{String(s.semana).padStart(2, '0')}: {fmtCorto(s.lunes)} → {fmtCorto(s.domingo)}
                                    </option>
                                  ))}
                                </optgroup>
                              );
                            })}
                          </>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className={labelBase}>Semana hasta</span>
                    <select
                      value={semanaHastaKey}
                      onChange={(e) => cambiarSemanaHasta(e.target.value)}
                      disabled={cargandoSemanas || semanasDisponibles.length === 0}
                      className={`mt-0.5 min-w-[240px] ${inputBase}`}
                    >
                      {cargandoSemanas ? <option value="">Cargando...</option>
                        : semanasDisponibles.length === 0 ? <option value="">Sin datos</option>
                        : (<>
                            <option value="">— Hasta —</option>
                            {aniosDisponibles.map((anio) => {
                              const semanasAnio = semanasDisponibles.filter((s) => s.year === anio);
                              return (
                                <optgroup key={anio} label={`Año ${anio}`}>
                                  {semanasAnio.map((s) => (
                                    <option key={s.key} value={s.key}>
                                      S{String(s.semana).padStart(2, '0')}: {fmtCorto(s.lunes)} → {fmtCorto(s.domingo)}
                                    </option>
                                  ))}
                                </optgroup>
                              );
                            })}
                          </>)}
                    </select>
                  </label>
                  {semDesde && semHasta && (
                    <div className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1 whitespace-nowrap">
                      📈 {semanaDesdeKey === semanaHastaKey
                        ? `Semana ${String(semDesde.semana).padStart(2, '0')} · ${semDesde.year}`
                        : `Sem ${String(semDesde.semana).padStart(2, '0')} → Sem ${String(semHasta.semana).padStart(2, '0')} · ${semDesde.year}`}
                      {' '}({fmtCorto(semDesde.lunes)} → {fmtCorto(semHasta.domingo)})
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Col 3: Sector / Piscina / Corrida */}
            <div className="flex flex-col gap-1.5 min-w-[180px]">
              <label className="flex flex-col">
                <span className={labelBase}>Sector</span>
                <select
                  value={sectorId}
                  onChange={(e) => setSectorId(e.target.value)}
                  className={`mt-0.5 ${inputBase}`}
                >
                  <option value="">-- Selecciona --</option>
                  {sectores.map((s) => (
                    <option key={s.id} value={s.id}>{s.nombre}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="flex flex-col gap-1.5 min-w-[180px]">
              <PiscinaBuscador
                piscinas={piscinas}
                value={piscinaId}
                onChange={(id) => setPiscinaId(id)}
                disabled={!sectorId}
              />
            </div>

            <div className="flex flex-col gap-1.5 min-w-[200px]">
              <label className="flex flex-col">
                <span className={labelBase}>Corrida</span>
                <select
                  value={siembraId}
                  onChange={(e) => setSiembraId(e.target.value)}
                  className={`mt-0.5 ${inputBase}`}
                  disabled={!piscinaId || corridas.length === 0}
                >
                  <option value="">
                    {corridas.length === 0 && piscinaId
                      ? 'Sin corridas'
                      : '-- Selecciona --'}
                  </option>
                  {corridas.map((c) => (
                    <option key={c.id} value={c.id}>
                      #{c.numero_corrida || '?'} - {format(new Date(c.fecha_siembra), 'dd/MM/yyyy')}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Botón limpiar (cuando hay filtro de fecha activo) */}
            {hayFiltroFecha && (
              <button
                onClick={limpiarFechas}
                className="text-[11px] text-gray-500 hover:text-gray-700 underline self-end mb-2"
              >
                ✕ Limpiar fechas
              </button>
            )}

            {/* Botón Excel */}
            <button
              type="button"
              onClick={exportarExcel}
              disabled={!piscinaId || !siembraId || (historial?.muestreos?.length || 0) === 0}
              title="Exportar a Excel"
              className="ml-auto self-end px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
              style={{ height: '42px' }}
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="2" fill="white" />
                <path
                  d="M8 8l8 8M16 8l-8 8"
                  stroke="#16A34A"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {!siembraId && (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          Selecciona un sector, una piscina y una corrida para ver el histórico.
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 rounded">{error}</div>
      )}

      {cargando && (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          Cargando...
        </div>
      )}

      {!cargando && siembraId && historial && !hayMuestreos && hayFiltroFecha && (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          No hay muestreos en el rango de fechas seleccionado.
        </div>
      )}

      {mostrarPanelCompleto && (
        <>
          {hayFiltroFecha && hayMuestreos && (
            <div className="mb-4 p-3 bg-blue-50 text-blue-900 rounded text-sm">
              📅 Mostrando muestreos {fechaDesde ? `desde ${formatearFechaCorta(fechaDesde)}` : ''}
              {fechaDesde && fechaHasta ? ' ' : ''}
              {fechaHasta ? `hasta ${formatearFechaCorta(fechaHasta)}` : ''} ·{' '}
              <strong>{historial.muestreos.length}</strong> registro{historial.muestreos.length !== 1 ? 's' : ''}
            </div>
          )}

          <div className="mb-3 flex justify-end">
            <button
              type="button"
              onClick={() => setMostrarResumen((v) => !v)}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-primary px-3 py-1.5 rounded hover:bg-gray-100"
              title={mostrarResumen ? 'Ocultar resumen' : 'Mostrar resumen'}
            >
              <span className="text-xs">{mostrarResumen ? '▼' : '▶'}</span>
              <span>{mostrarResumen ? 'Ocultar resumen' : 'Mostrar resumen'}</span>
            </button>
          </div>

          {mostrarResumen && (
            <>
              {historial.siembra && (
                <div className="bg-blue-50 p-4 rounded shadow mb-6">
                  <h3 className="font-semibold text-blue-900 mb-2">
                    Datos de la corrida #{historial.siembra.numero_corrida || '?'}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600">Fecha siembra</div>
                      <div className="font-semibold">
                        {format(new Date(historial.siembra.fecha_siembra), 'dd/MM/yyyy')}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">Cantidad sembrada</div>
                      <div className="font-semibold">
                        {Number(historial.siembra.cantidad_sembrada).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">Peso inicial</div>
                      <div className="font-semibold">
                        {historial.siembra.peso_inicial_g
                          ? `${Number(historial.siembra.peso_inicial_g).toFixed(2)} g`
                          : '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">Días de cultivo</div>
                      <div className="font-semibold">{diasCultivo ?? '-'} días</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                <KPI
                  label="Último peso"
                  value={kpiUltimoPeso != null ? `${Number(kpiUltimoPeso).toFixed(2)} g` : '-'}
                  icon="⚖️"
                  color="blue"
                />
                <KPI
                  label="Crecimiento lineal"
                  value={kpiCrecLineal != null ? `${kpiCrecLineal.toFixed(2)} g/sem` : '-'}
                  icon="📐"
                  color="indigo"
                />
                <KPI
                  label={hayMuestreos ? 'Animales último muestreo' : 'Animales sembrados'}
                  value={kpiAnimales != null ? Number(kpiAnimales).toLocaleString() : '-'}
                  icon="🦐"
                  color="green"
                />
                <KPI
                  label="Ganancia desde siembra"
                  value={kpiGanancia != null ? `${kpiGanancia.toFixed(2)} g` : '-'}
                  icon="📈"
                  color="orange"
                />
                <KPI
                  label="Última sobrevivencia"
                  value={kpiSobrevivencia != null
                    ? `${Math.round(kpiSobrevivencia)}%`
                    : 'pendiente'}
                  icon="💯"
                  color="purple"
                />
              </div>
            </>
          )}

          {/* Gráfico de peso + FCA */}
          {datosGrafico.length > 0 && (
            <div className="bg-white p-4 rounded shadow mb-6">
              <h3 className="font-semibold text-lg mb-4">
                Evolución del peso promedio{hayFca ? ' y FCA' : ''}
              </h3>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={datosGrafico} margin={{ top: 5, right: 60, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fecha" />
                  <YAxis
                    yAxisId="left"
                    label={{ value: 'g', angle: -90, position: 'insideLeft' }}
                  />
                  {hayFca && (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      label={{ value: 'FCA', angle: 90, position: 'insideRight' }}
                      tickFormatter={(v) => Number(v).toFixed(2)}
                    />
                  )}
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-white p-2 border rounded shadow text-sm">
                          <div><strong>{d.fechaCompleta}</strong></div>
                          <div>Peso: {d.peso} g</div>
                          {d.tipo === 'siembra' ? (
                            <div className="text-blue-700">
                              Siembra: {Number(d.animales).toLocaleString()} animales
                            </div>
                          ) : (
                            <>
                              <div>Animales muestreados: {Number(d.animales).toLocaleString()}</div>
                              {d.fca != null && (
                                <div className="text-purple-700">
                                  FCA: <strong>{Number(d.fca).toFixed(3)}</strong>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="peso"
                    stroke="#0D47A1"
                    strokeWidth={2}
                    name="Peso (g)"
                    dot={(props) => {
                      const { cx, cy, payload, index } = props;
                      const esSiembra = payload.tipo === 'siembra';
                      return (
                        <circle
                          key={index}
                          cx={cx}
                          cy={cy}
                          r={esSiembra ? 6 : 4}
                          fill={esSiembra ? '#2E7D32' : '#0D47A1'}
                          stroke="#fff"
                          strokeWidth={2}
                        />
                      );
                    }}
                  />
                  {hayFca && (
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="fca"
                      stroke="#9333EA"
                      strokeWidth={2}
                      strokeDasharray="5 3"
                      name="FCA"
                      connectNulls
                      dot={{ r: 4, fill: '#9333EA', stroke: '#fff', strokeWidth: 2 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Gráfico de sobrevivencia */}
          {datosGrafico.length > 0 && (
            <div className="bg-white p-4 rounded shadow mb-6">
              <h3 className="font-semibold text-lg mb-4">
                Evolución del porcentaje de sobrevivencia
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={datosGrafico}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fecha" />
                  <YAxis
                    domain={[0, 100]}
                    label={{ value: '%', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-white p-2 border rounded shadow text-sm">
                          <div><strong>{d.fechaCompleta}</strong></div>
                          <div>Sobrevivencia: {d.porcentaje}%</div>
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="porcentaje"
                    stroke="#2E7D32"
                    strokeWidth={2}
                    name="Sobrevivencia (%)"
                    dot={(props) => {
                      const { cx, cy, payload, index } = props;
                      const esSiembra = payload.tipo === 'siembra';
                      return (
                        <circle
                          key={index}
                          cx={cx}
                          cy={cy}
                          r={esSiembra ? 6 : 4}
                          fill={esSiembra ? '#1565C0' : '#2E7D32'}
                          stroke="#fff"
                          strokeWidth={2}
                        />
                      );
                    }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Tabla de muestreos */}
          {hayMuestreos ? (
            <div className="bg-white rounded shadow overflow-hidden">
              <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                <h3 className="font-semibold text-lg">
                  Muestreos de la corrida #{historial.siembra?.numero_corrida || '?'} ({historial.muestreos.length})
                </h3>
                {hayFiltroFecha && (
                  <span className="text-xs text-gray-500">
                    Filtro de fechas activo
                  </span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Fecha</th>
                      <th className="text-right px-3 py-2 font-semibold">Peso (g)</th>
                      <th className="text-right px-3 py-2 font-semibold">Animales</th>
                      <th className="text-right px-3 py-2 font-semibold">Crec. lineal (g/sem)</th>
                      <th className="text-right px-3 py-2 font-semibold">F. C. A</th>
                      <th className="text-right px-3 py-2 font-semibold">Sobrev. (%)</th>
                      <th className="text-left px-3 py-2 font-semibold">Observaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historial.muestreos.map((m) => {
                      const crecLineal = calcCrecLineal(m.peso_promedio_g, m.fecha_hora);
                      return (
                        <tr key={m.id} className="border-t hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap">
                            {format(new Date(m.fecha_hora), 'dd/MM/yyyy HH:mm')}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {Number(m.peso_promedio_g).toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-right">{m.cantidad_muestreada}</td>
                          <td className="px-3 py-2 text-right text-gray-700">
                            {crecLineal != null ? crecLineal.toFixed(2) : '-'}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold">
                            {m.fca != null
                              ? <span className="text-purple-700">{Number(m.fca).toFixed(3)}</span>
                              : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold">
                            {m.porcentaje != null
                              ? <span className="text-green-700">{Math.round(Number(m.porcentaje))}%</span>
                              : <span className="text-gray-400 text-xs">pendiente</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-600 max-w-xs truncate">
                            {m.observaciones || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white p-6 rounded shadow text-center text-gray-500">
              Esta corrida aún no tiene muestreos registrados. Se muestra la siembra con 100% de sobrevivencia.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KPI({ label, value, icon, color = 'blue' }) {
  const colores = {
    blue: 'bg-blue-50 text-blue-900',
    green: 'bg-green-50 text-green-900',
    orange: 'bg-orange-50 text-orange-900',
    purple: 'bg-purple-50 text-purple-900',
    indigo: 'bg-indigo-50 text-indigo-900',
  };
  return (
    <div className={`p-4 rounded shadow ${colores[color] || colores.blue}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-medium opacity-75">{label}</div>
          <div className="text-xl font-bold mt-1">{value}</div>
        </div>
        <div className="text-3xl opacity-50">{icon}</div>
      </div>
    </div>
  );
}